import React, { useState, useMemo } from 'react';
import { Table, Checkbox, Switch, Slider, Button, Empty, Tooltip, Space, Row, Col, Typography, Tag, Badge } from 'antd';
import { CheckOutlined, WarningOutlined, ExclamationCircleOutlined, SwapOutlined, ContainerOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { SailingResult, TransportMode } from './types';
import { Card } from '../design-system';
import { colors, spacing } from '../design-system/tokens';
import { format, differenceInDays, parseISO } from 'date-fns';

const { Text } = Typography;

const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  VESSEL: 'Ship',
  BARGE: 'Barge',
  RAIL: 'Rail',
  TRUCK: 'Truck',
  RAIL_TRUCK: 'Rail/Truck',
  BARGE_TRUCK: 'Barge/Truck',
  BARGE_RAIL: 'Barge/Rail',
  MULTIMODAL: 'Multi',
};

const TRANSPORT_MODE_COLORS: Record<TransportMode, string> = {
  VESSEL: colors.primary[60],
  BARGE: '#8B4513',
  RAIL: '#2F4F4F',
  TRUCK: '#FF8C00',
  RAIL_TRUCK: '#6B8E23',
  BARGE_TRUCK: '#CD853F',
  BARGE_RAIL: '#708090',
  MULTIMODAL: '#9370DB',
};

const PORT_CODE_TO_CITY: Record<string, { city: string; country: string }> = {
  KRPUS: { city: 'Pusan', country: 'Korea' },
  KRBUS: { city: 'Busan', country: 'Korea' },
  KRINC: { city: 'Incheon', country: 'Korea' },
  CNSHA: { city: 'Shanghai', country: 'China' },
  CNNBO: { city: 'Ningbo', country: 'China' },
  CNQIN: { city: 'Qingdao', country: 'China' },
  CNYTN: { city: 'Yantian', country: 'China' },
  HKHKG: { city: 'Hong Kong', country: 'Hong Kong' },
  TWKHH: { city: 'Kaohsiung', country: 'Taiwan' },
  TWTPE: { city: 'Taipei', country: 'Taiwan' },
  SGSIN: { city: 'Singapore', country: 'Singapore' },
  MYTPP: { city: 'Tanjung Pelepas', country: 'Malaysia' },
  MYPKG: { city: 'Port Klang', country: 'Malaysia' },
  JPTYO: { city: 'Tokyo', country: 'Japan' },
  JPYOK: { city: 'Yokohama', country: 'Japan' },
  JPKOB: { city: 'Kobe', country: 'Japan' },
  JPOSA: { city: 'Osaka', country: 'Japan' },
  JPNGO: { city: 'Nagoya', country: 'Japan' },
  JPHKT: { city: 'Hakata', country: 'Japan' },
  JPSMZ: { city: 'Shimizu', country: 'Japan' },
};

const formatPortDisplay = (portCode: string, portName: string): string => {
  const known = PORT_CODE_TO_CITY[portCode?.toUpperCase()];
  if (known) {
    return known.country === 'Japan' ? known.city : `${known.city}, ${known.country}`;
  }
  
  let cleanName = portName || portCode || '';
  const parts1 = cleanName.split('(');
  cleanName = (parts1[0] || '').trim();
  const parts2 = cleanName.split(',');
  cleanName = (parts2[0] || '').trim();
  cleanName = cleanName.split(' ').map(w => 
    w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''
  ).join(' ');
  
  if (portCode?.toUpperCase().startsWith('JP')) {
    return cleanName;
  }
  
  return cleanName;
};

interface SailingResultsTableProps {
  results: SailingResult[];
  onSelect: (sailing: SailingResult) => void;
  cutoffBufferDays?: number;
}

type Carrier = 'ONE' | 'YM' | 'HMM';

const getCarrier = (vesselName: string): Carrier => {
  if (!vesselName) return 'ONE';
  const name = vesselName.toUpperCase();
  if (name.startsWith('YM ') || name.startsWith('YM-')) return 'YM';
  if (name.startsWith('HMM ')) return 'HMM';
  return 'ONE';
};

/**
 * Check if destination port is Japan based on UN/LOCODE
 * Japan ports start with "JP"
 */
const isJapanDestination = (destinationPort: string): boolean => {
  return destinationPort.toUpperCase().startsWith('JP');
};

/**
 * Check if a port is in Japan based on UN/LOCODE
 */
const isJapanPort = (portCode: string): boolean => {
  return portCode.toUpperCase().startsWith('JP');
};

/**
 * Check for tight cutoff warning
 * Returns true if CY Cut is less than buffer days from today
 */
const hasTightCutoffWarning = (cyCut: string | null, bufferDays: number): boolean => {
  if (!cyCut) return false;
  const daysUntilCut = differenceInDays(parseISO(cyCut), new Date());
  return daysUntilCut < bufferDays;
};

const hasJapanTransshipmentWarning = (
  destinationPort: string,
  isDirect: boolean,
  transshipments: { port: string; portCode: string }[]
): boolean => {
  if (!isJapanDestination(destinationPort)) return false;
  if (isDirect || !transshipments || transshipments.length === 0) return false;
  return transshipments.some(ts => ts.portCode && !isJapanPort(ts.portCode));
};

const hasNonJapanTransshipment = (transshipments: { portCode: string }[]): boolean => {
  if (!transshipments || transshipments.length === 0) return false;
  return transshipments.some(ts => ts.portCode && !isJapanPort(ts.portCode));
};

export const SailingResultsTable: React.FC<SailingResultsTableProps> = ({
  results,
  onSelect,
  cutoffBufferDays = 10,
}) => {
  const [selectedCarriers, setSelectedCarriers] = useState<Carrier[]>(['ONE']);
  const [showDirectOnly, setShowDirectOnly] = useState(false);
  const [showJapanOnly, setShowJapanOnly] = useState(false);
  const [bufferDays, setBufferDays] = useState(cutoffBufferDays);

  const filteredData = useMemo(() => {
    return results.filter((sailing) => {
      const carrier = getCarrier(sailing.vesselName);
      if (!selectedCarriers.includes(carrier)) return false;

      if (showDirectOnly && !sailing.isDirect) return false;

      if (showJapanOnly && hasNonJapanTransshipment(sailing.transshipments || [])) return false;

      if (sailing.cyCut) {
        const daysUntilCut = differenceInDays(parseISO(sailing.cyCut), new Date());
        if (daysUntilCut < bufferDays) return false;
      }

      return true;
    });
  }, [results, selectedCarriers, showDirectOnly, showJapanOnly, bufferDays]);

  const handleCarrierChange = (checkedValues: any[]) => {
    setSelectedCarriers(checkedValues as Carrier[]);
  };

   const columns: ColumnsType<SailingResult> = [
     {
       title: '',
       key: 'status',
       width: 70,
       render: (_, record) => {
         const tightCutoff = hasTightCutoffWarning(record.cyCut, bufferDays);
         const japanTransship = hasJapanTransshipmentWarning(
           record.destinationPort,
           record.isDirect,
           record.transshipments || []
         );
         const bookedCount = record.bookedShipments?.length || 0;

         return (
           <Space size={4}>
             {bookedCount > 0 && (
               <Tooltip 
                 title={
                   <div>
                     <div style={{ fontWeight: 600, marginBottom: 4 }}>Booked Shipments:</div>
                     {record.bookedShipments.map((s, i) => (
                       <div key={i}>{s.reference}{s.buyer ? ` (${s.buyer})` : ''}</div>
                     ))}
                   </div>
                 }
               >
                 <Badge 
                   count={bookedCount} 
                   size="small"
                   style={{ backgroundColor: colors.primary[60] }}
                 >
                   <ContainerOutlined style={{ fontSize: '14px', color: colors.primary[60] }} />
                 </Badge>
               </Tooltip>
             )}
             {tightCutoff && (
               <Tooltip title={`CY Cut is within ${bufferDays} days - may be too tight for supplier preparation`}>
                 <WarningOutlined style={{ color: colors.status.warning, fontSize: '16px' }} />
               </Tooltip>
             )}
             {japanTransship && (
               <Tooltip title="Transshipment outside Japan for Japan-bound cargo may require additional aflatoxin testing">
                 <ExclamationCircleOutlined style={{ color: colors.status.negative, fontSize: '16px' }} />
               </Tooltip>
             )}
           </Space>
         );
       },
     },
     {
       title: 'Vessel',
       key: 'vessel',
       sorter: (a, b) => a.vesselName.localeCompare(b.vesselName),
       render: (_, record) => {
         const voyageShort = record.vvd ? record.vvd.slice(-4) : '';
         return (
           <div style={{ fontWeight: 600, color: colors.text.primary }}>
             {record.vesselName}{voyageShort ? ` / ${voyageShort}` : ''}
           </div>
         );
       },
     },
    {
      title: 'Route',
      key: 'transship',
      width: 160,
      render: (_, record) => {
        if (record.isDirect) {
          return <Text type="success"><CheckOutlined /> Direct</Text>;
        }
        
        const transshipments = record.transshipments || [];
        if (transshipments.length === 0 && record.transshipmentPorts.length > 0) {
          return (
            <span style={{ color: colors.text.secondary }}>
              <SwapOutlined /> {record.transshipmentPorts.length} T/S
            </span>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {transshipments.map((ts, idx) => {
              const displayName = formatPortDisplay(ts.portCode, ts.port);
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tag 
                    color={TRANSPORT_MODE_COLORS[ts.mode]} 
                    style={{ margin: 0, fontSize: '10px', lineHeight: '16px', padding: '0 4px' }}
                  >
                    {TRANSPORT_MODE_LABELS[ts.mode]}
                  </Tag>
                  <span style={{ fontSize: '12px', color: colors.text.secondary }}>
                    {displayName}
                  </span>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: 'ETD',
      dataIndex: 'etd',
      key: 'etd',
      sorter: (a, b) => new Date(a.etd).getTime() - new Date(b.etd).getTime(),
      render: (text) => (
        <span style={{ color: colors.text.primary }}>
          {format(parseISO(text), 'MMM dd')}
        </span>
      ),
    },
    {
      title: 'ETA',
      dataIndex: 'eta',
      key: 'eta',
      sorter: (a, b) => new Date(a.eta).getTime() - new Date(b.eta).getTime(),
      render: (text) => (
        <span style={{ color: colors.text.primary }}>
          {format(parseISO(text), 'MMM dd')}
        </span>
      ),
    },
    {
      title: 'Cutoff',
      dataIndex: 'cyCut',
      key: 'cyCut',
      sorter: (a, b) => {
        if (!a.cyCut) return 1;
        if (!b.cyCut) return -1;
        return new Date(a.cyCut).getTime() - new Date(b.cyCut).getTime();
      },
      render: (text, record) => {
        if (!text) return <span style={{ color: colors.text.tertiary }}>-</span>;
        
        const cutoffDate = parseISO(text);
        const daysUntil = differenceInDays(cutoffDate, new Date());
        const isUrgent = daysUntil < 7;
        const isTight = daysUntil < 14;
        
        return (
          <div>
            <div style={{ 
              color: isUrgent ? colors.status.negative : isTight ? colors.status.warning : colors.text.primary, 
              fontWeight: 600 
            }}>
              {format(cutoffDate, 'MMM dd')}
            </div>
            <div style={{ fontSize: '11px', color: colors.text.tertiary }}>
              {format(cutoffDate, 'HH:mm')}
            </div>
            {record.docsCut && (
              <Tooltip title={`Docs: ${format(parseISO(record.docsCut), 'MMM dd HH:mm')}`}>
                <div style={{ fontSize: '10px', color: colors.text.tertiary, marginTop: '2px' }}>
                  +Docs
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Transit',
      dataIndex: 'transitTime',
      key: 'transit',
      sorter: (a, b) => a.transitTime - b.transitTime,
      render: (days) => (
        <span style={{ fontWeight: 500 }}>
          {days} days
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small"
          onClick={() => onSelect(record)}
          style={{ 
            backgroundColor: colors.primary[60],
            borderColor: colors.primary[60],
            boxShadow: 'none'
          }}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <Card>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={8}>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, color: colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Carriers
            </div>
            <Checkbox.Group 
              options={['ONE', 'YM', 'HMM']} 
              value={selectedCarriers} 
              onChange={handleCarrierChange}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} md={8}>
            <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, color: colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Route Type
            </div>
            <Space direction="vertical" size={4}>
              <Space>
                <Switch 
                  checked={showDirectOnly} 
                  onChange={(checked) => {
                    setShowDirectOnly(checked);
                    if (checked) setShowJapanOnly(false);
                  }} 
                  size="small"
                />
                <span style={{ color: colors.text.primary }}>Direct Only</span>
              </Space>
              <Space>
                <Switch 
                  checked={showJapanOnly} 
                  onChange={(checked) => {
                    setShowJapanOnly(checked);
                    if (checked) setShowDirectOnly(false);
                  }} 
                  size="small"
                />
                <span style={{ color: colors.text.primary }}>Japan T/S Only</span>
              </Space>
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Min. Days to Cutoff
              </span>
              <span style={{ fontSize: '12px', color: colors.primary[60], fontWeight: 600 }}>
                {bufferDays} days
              </span>
            </div>
            <Slider
              min={1}
              max={30}
              value={bufferDays}
              onChange={setBufferDays}
              tooltip={{ formatter: (value) => `${value} days` }}
              trackStyle={{ backgroundColor: colors.primary[60] }}
              handleStyle={{ borderColor: colors.primary[60], boxShadow: 'none' }}
            />
          </Col>
        </Row>
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record, index) => `${record.vvd}-${record.etd}-${index}`}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: colors.text.secondary }}>
                    No sailings match the selected filters
                  </span>
                }
              />
            )
          }}
          rowClassName={() => 'sailing-row'}
          style={{
            border: 'none'
          }}
        />
      </Card>
      
      <style>{`
        .sailing-row:hover td {
          background-color: ${colors.background.secondary} !important;
        }
        .ant-table-thead > tr > th {
          background: ${colors.background.secondary};
          color: ${colors.text.secondary};
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
        }
      `}</style>
    </div>
  );
};
